import React, { useState } from 'react';
import { Text } from '@gjensidige/builders-components';
import {
  CalculationWidget,
  CalculationWidgetProps
} from '@gjensidige/se-common-calculation';
import { validateSSN } from '@gjensidige/se-helpers';

import { checkEligibility, fetchPropertyData } from '../api-config/index';
import { CustomErrorModal } from '../components/CustomErrorModal';
import { PropertyIdExplanationModal } from '../components/PropertyIdExplanationModal';
import { WidgetProps } from '../interfaces/Types';
import { AppHelper } from '../utils/AppHelper';
import {
  ageResolver,
  collapsePropertyIdWhitespace,
  collapseWhitespace,
  removeKommunPostfix
} from '../utils/CalculationHelper';
import { logInfoMessage } from '../utils/LogHelper';
import {
  isVolontaryPhoneValid,
  MIN_AGE,
  validateAddress,
  validateCprAndAge,
  validateMunicipality,
  validatePropertyName,
  validateZipCode
} from '../utils/Validations';
interface InputWidgetProps {
  enonicData: WidgetProps;
}

export const InputWidgetContainer: React.FC<InputWidgetProps> = ({
  enonicData
}: InputWidgetProps) => {
  /* PERSON_NUMBER */
  const [personNumber, setPersonNumber] = useState('');
  const [isValidPersonNumber, setIsValidPersonNumber] = useState(false);
  const [personNumberErrorText, setPersonNumberErrorText] = useState('');
  /* ZIP_CODE */
  const [zipCode, setZipCode] = useState('');
  const [validZipCode, isValidZipCode] = useState(false);
  const [zipCodeErrorText, setZipCodeErrorText] = useState('');
  /* ADDRESS_TYPE */
  const [addressType, setAddressType] = useState(AppHelper.streetAddress);
  /* STREET_ADDRESS */
  const [address, setAddress] = useState('');
  const [validAddress, setValidAddress] = useState(false);
  const [addressErrorText, setAddressErrorText] = useState('');
  /* PROPERTY_NAME */
  const [propertyKey, setPropertyKey] = useState('');
  const [validPropertyName, setValidPropertyName] = useState(false);
  const [propertyNameErrorText, setPropertyNameErrorText] = useState('');
  /* MUNICIPALITY */
  const [municipality, setMunicipality] = useState('');
  const [validMunicipality, setValidMunicipality] = useState(false);
  const [municipalityErrorText, setMunicipalityErrorText] = useState('');
  /* PHONE NR */
  const [phoneNumber, setPhoneNumber] = useState('');
  const [validPhoneNumber, setValidPhoneNumber] = useState(true);
  /* MODALS */
  const [propertyIdExplanationModalOpen, setPropertyIdExplanationModalOpen] =
    useState(false);
  const [hasCustomerError, setHasCustomerError] = useState(false);
  const [errorObject, setErrorObject] = useState({
    error: false,
    title: AppHelper.defaultErrorTitle,
    message: AppHelper.defaultErrorMessage,
    statusCode: AppHelper.defaultErrorStatusCode
  });
  /* OTHER */
  const [isPropertyDataLoading, setIsPropertyDataLoading] = useState(false);
  const [isCprValidationLoading, setIsCprValidationLoading] = useState(false);

  const handleOnChangePersonNumber = async (event: any) => {
    event.preventDefault();
    const customerInput = event.currentTarget.value.replace(/\D/g, '');
    setPersonNumber(customerInput.toUpperCase());
    const valid = validateCprAndAge({ age: MIN_AGE }, customerInput);
    setIsValidPersonNumber(valid);
    if (valid) {
      setIsCprValidationLoading(true);
      const response = await checkEligibility(customerInput);
      if (response?.error) {
        logInfoMessage(
          `error - property data - eligibility check - ${
            response?.message || 'something went wrong'
          } - statusCode: ${
            response?.statusCode || AppHelper.defaultErrorStatusCode
          }`
        );
        setHasCustomerError(true);
        setErrorObject({
          error: false,
          title: response?.title || AppHelper.defaultErrorTitle,
          message: response?.message || AppHelper.defaultErrorMessage,
          statusCode: response?.statusCode || AppHelper.defaultErrorStatusCode
        });
      } else {
        setHasCustomerError(false);
      }
      setIsCprValidationLoading(false);
    }
    if (!customerInput.length) {
      setPersonNumberErrorText('');
    }
  };

  const handleOnBlurPersonNumber = async (event: any) => {
    event.preventDefault();
    const customerInput = event.currentTarget.value.replace(/\D/g, '');
    if (!customerInput.length) {
      setPersonNumberErrorText('');
    } else {
      if (isValidPersonNumber) {
        setPersonNumberErrorText('');
      }
      if (!validateSSN(customerInput)) {
        setPersonNumberErrorText(AppHelper.pcrNumberErrorText);
      } else if (ageResolver(customerInput) < MIN_AGE) {
        setPersonNumberErrorText(AppHelper.pcrMinAgeErrorText);
      }
    }
  };

  const handleZipCodeChange = (event: any) => {
    const customerInput = event.currentTarget.value.replace(/\D/g, '');
    setZipCode(customerInput);
    const isZipValid = validateZipCode(customerInput);
    isValidZipCode(isZipValid);
    if (!customerInput.length) {
      setZipCodeErrorText('');
    }
  };

  const handleAddressType = (value: string) => {
    setAddressType(value);
  };

  const handleAddressChange = (event: any) => {
    const customerInput = event.currentTarget.value.trim();
    if (customerInput) {
      setAddress(event.currentTarget.value);
    } else {
      setAddress(customerInput);
    }
    const isAddressValid = validateAddress(event.currentTarget.value);
    setValidAddress(isAddressValid);
  };

  const handleOnBlurAddress = (event: any) => {
    const customerInput = event.currentTarget.value;
    const isAddressValid = validateAddress(event.currentTarget.value);
    if (!isAddressValid && customerInput.length) {
      setAddressErrorText(AppHelper.streetAddressErrorText);
    } else {
      setAddressErrorText('');
    }
  };

  const handleMunicipalityChange = (event: any) => {
    const customerInput = event.currentTarget.value.trim().replace('.', '');
    if (customerInput) {
      setMunicipality(event.currentTarget.value.replace('.', ''));
    } else {
      setMunicipality(customerInput);
    }

    const isMunicipalityValid = validateMunicipality(
      event.currentTarget.value.replace('.', '')
    );
    setValidMunicipality(isMunicipalityValid);
  };

  const handleOnBlurMunicipality = (event: any) => {
    const customerInput = event.currentTarget.value;
    const isMunicipalityValid = validateMunicipality(event.currentTarget.value);
    if (!isMunicipalityValid && customerInput.length) {
      setMunicipalityErrorText(AppHelper.municipalityErrorText);
    } else {
      setMunicipalityErrorText('');
    }
  };

  const handlePropertyNameChange = (event: any) => {
    const customerInput = event.currentTarget.value.trim();
    if (customerInput) {
      setPropertyKey(event.currentTarget.value);
    } else {
      setPropertyKey(customerInput);
    }

    const isPropertyIdNameValid = validatePropertyName(
      event.currentTarget.value
    );
    setValidPropertyName(isPropertyIdNameValid);
  };

  const handleOnBlurPropertyName = (event: any) => {
    const customerInput = event.currentTarget.value;
    const isPropertyIdNameValid = validatePropertyName(
      event.currentTarget.value
    );
    if (!isPropertyIdNameValid && customerInput.length) {
      setPropertyNameErrorText(AppHelper.propertyIdErrorText);
    } else {
      setPropertyNameErrorText('');
    }
  };

  const handleOnBlurZipCode = (event: any) => {
    const customerInput = event.currentTarget.value.replace(/\D/g, '');
    if (!customerInput.length) {
      setZipCodeErrorText('');
    } else {
      if (validZipCode) {
        setZipCodeErrorText('');
      } else {
        if (!validateZipCode(customerInput)) {
          setZipCodeErrorText(AppHelper.zipCodeErrorText);
        }
      }
    }
  };

  const handleOnChangePhoneNumber = (event: any) => {
    const customerInput = event.currentTarget.value.trim();
    setPhoneNumber(customerInput);

    if (!validPhoneNumber) {
      const isPhoneValid = isVolontaryPhoneValid(event.currentTarget.value);
      setValidPhoneNumber(isPhoneValid);
    }
  };

  const handleOnBlurPhoneNumber = (event: any) => {
    const customerInput = event.currentTarget.value.trim();
    setPhoneNumber(customerInput);

    const isPhoneValid = isVolontaryPhoneValid(customerInput);
    if (!isPhoneValid) {
      setValidPhoneNumber(isPhoneValid);
      sessionStorage.removeItem(AppHelper.CUSTOMER_PHONE_NR_SESSION_KEY);
    } else {
      setValidPhoneNumber(isPhoneValid);
      sessionStorage.setItem(
        AppHelper.CUSTOMER_PHONE_NR_SESSION_KEY,
        phoneNumber
      );
    }
  };

  const isPrimaryButtonDisabled = () => {
    if (addressType === AppHelper.propertyId) {
      return !validPropertyName || !validMunicipality;
    }
    if (addressType === AppHelper.streetAddress) {
      return !validZipCode || !validAddress;
    }
    return false;
  };

  const onCloseErrorModal = () => {
    setIsPropertyDataLoading(false);
    setErrorObject({
      ...errorObject,
      error: false
    });
  };

  const gotoNextStep = async (event: any) => {
    event.preventDefault();

    setIsPropertyDataLoading(true);

    if (hasCustomerError) {
      setErrorObject(
        (prev) =>
          (prev = {
            ...prev,
            error: true
          })
      );
      return;
    }

    const isPropertyId = addressType === AppHelper.propertyId;
    const FormattedPropertyKey = isPropertyId
      ? collapsePropertyIdWhitespace(collapseWhitespace(propertyKey)).trim()
      : '';
    const formattedMunicipality = isPropertyId
      ? collapseWhitespace(removeKommunPostfix(municipality)).trim()
      : '';
    const formattedAddress = !isPropertyId
      ? collapseWhitespace(address).trim()
      : '';
    const formattedZipCode = !isPropertyId
      ? collapseWhitespace(zipCode).trim()
      : '';
    const propertyId = `${formattedMunicipality} ${FormattedPropertyKey}`;

    const customerInfo = {
      cprNumber: personNumber,
      customerAddress: formattedAddress,
      propertyId: isPropertyId ? propertyId : '',
      propertyNameType: addressType,
      zipCode: formattedZipCode
    };

    sessionStorage.setItem('customerInfo', JSON.stringify(customerInfo));

    const streetAddressKey = `${formattedAddress} ${formattedZipCode}`;
    const propertyQuery = isPropertyId
      ? propertyId.toUpperCase()
      : streetAddressKey;
    const response = await fetchPropertyData(isPropertyId, propertyQuery);

    if (response?.calculationStatus?.error) {
      setErrorObject({
        error: true,
        title:
          response?.calculationStatus?.title || AppHelper.defaultErrorTitle,
        message:
          response?.calculationStatus?.message || AppHelper.defaultErrorMessage,
        statusCode:
          response?.calculationStatus?.statusCode ||
          AppHelper.defaultErrorStatusCode
      });
      logInfoMessage(
        `error - property data - property fetch - ${
          response?.calculationStatus?.message || 'something went wrong'
        } - statusCode: ${
          response?.calculationStatus?.statusCode ||
          AppHelper.defaultErrorStatusCode
        }`
      );
      return;
    }

    if (response?.propertyDataList && response.propertyDataList.length > 0) {
      const property = response.propertyDataList[0];

      const showExtraInfoCaseOne =
        property.addresses === 1 &&
        property.mainBuildings === 2 &&
        property.valueUnits === 1;
      const showExtraInfoCaseTwo =
        property.addresses === 1 &&
        property.mainBuildings === 1 &&
        property.valueUnits === 2;
      const showExtraInfoCaseThree =
        property.addresses === 1 &&
        property.mainBuildings === 2 &&
        property.valueUnits === 2;
      const showExtraInfoToWebshop =
        showExtraInfoCaseOne || showExtraInfoCaseTwo || showExtraInfoCaseThree;

      const CustomerWithPropertyData = {
        ...customerInfo,
        propertyId: property?.propertyIdTextUnique || '',
        customerAddress: property?.address || '',
        zipCode: property?.postNumber ? property.postNumber.toString() : '',
        noOfProperties: showExtraInfoToWebshop ? '2' : '1',
        propertyData: {
          propertyKey: property?.propertyIdText || '',
          area: property?.area ? property?.area.toString() : '',
          builtYear: property?.builtYear ? property?.builtYear.toString() : '',
          ancillaryArea: property?.ancillaryArea
            ? property?.ancillaryArea.toString()
            : ''
        }
      };
      logInfoMessage('success - property data - redirected to webshop');
      sessionStorage.setItem(
        'customerInfo',
        JSON.stringify(CustomerWithPropertyData)
      );
      window.location.href = window.location.origin + AppHelper.webshopUrl;
    } else {
      setErrorObject({
        error: true,
        title: AppHelper.defaultErrorTitle,
        message: AppHelper.defaultErrorMessage,
        statusCode: AppHelper.defaultErrorStatusCode
      });
    }
  };

  const getCprMoreInfoContent = () => {
    return (
      <Text>
        Vi behöver ditt personnummer för att kunna ge dig rätt pris.
        Personnumret ger oss bland annat uppgifter om var du bor och din ålder.
      </Text>
    );
  };

  // To disable phone number in form toggle this to false.
  const showVolontaryPhoneNumber = true;

  const homeParams: CalculationWidgetProps = {
    headerText: enonicData.headerText,
    image: AppHelper.getImageCDNPath(enonicData.imageInfo.image),
    smallScreenImage: AppHelper.getImageCDNPath(
      enonicData.imageInfo.smallScreenImage
    ),
    imageAltText: enonicData.imageInfo.imageAltText,
    inputLists: {
      calculationInputs: [
        {
          type: 'inputText',
          inputListInRow: [
            {
              id: 'cpr-number',
              labelText: enonicData.cprNumberLabel,
              name: 'input-cprNumber',
              onChange: handleOnChangePersonNumber,
              onBlur: handleOnBlurPersonNumber,
              value: personNumber,
              helpText: '12 siffror ÅÅÅÅMMDDNNNN.',
              isHelpTextAlwaysVisible: true,
              moreInfoModal: {
                linkText: 'Varför frågar vi om personnummer?',
                title: 'Personnummer',
                content: getCprMoreInfoContent(),
                closeLabel: 'Stäng hjälptext',
                ariaLabel: 'Hjälp om personnummer',
                wrapperClassName: 'cpr-modal-wrapper',
                ariaDescribedBy: 'cpr-more-info'
              },
              className: 'mb-1',
              labelClassName: 'input-label-text',
              inputWrapperClassName: 'ssn',
              invalid: !isValidPersonNumber && !!personNumberErrorText,
              errorText: personNumberErrorText,
              inputMode: 'numeric'
            }
          ]
        },
        {
          type: 'inputRadio',
          wrapperClassName: 'input-group-row',
          preSelectedValue: AppHelper.streetAddress,
          inputListInRow: [
            {
              id: 'street-address-radio',
              labelText: enonicData.addressLabel,
              name: AppHelper.streetAddress,
              onChange: handleAddressType,
              value: AppHelper.streetAddress,
              labelClassName: 'input-label-text',
              invalid: false,
              errorText: ''
            },
            {
              id: 'property-name-radio',
              labelText: enonicData.propertyNameLabel,
              name: AppHelper.propertyId,
              onChange: handleAddressType,
              value: AppHelper.propertyId,
              labelClassName: 'input-label-text',
              invalid: false,
              errorText: ''
            }
          ]
        },
        {
          type: 'inputText',
          inputListInRow:
            addressType === AppHelper.streetAddress
              ? [
                  {
                    id: 'gatuaddress',
                    labelText: enonicData.addressLabel,
                    name: 'input-gatuaddress',
                    onChange: handleAddressChange,
                    onBlur: handleOnBlurAddress,
                    value: address,
                    labelClassName: 'input-label-text',
                    inputWrapperClassName: 'street-address',
                    invalid: !validAddress && !!addressErrorText,
                    errorText: addressErrorText,
                    autoCompleteLabel: 'street-address'
                  }
                ]
              : [
                  {
                    id: 'kommun',
                    labelText: enonicData.municipalityLabel,
                    name: 'input-kommun',
                    onChange: handleMunicipalityChange,
                    onBlur: handleOnBlurMunicipality,
                    value: municipality,
                    labelClassName: 'input-label-text',
                    inputWrapperClassName: 'municipality',
                    invalid: !validMunicipality && !!municipalityErrorText,
                    errorText: municipalityErrorText,
                    autoCompleteLabel: 'municipality'
                  }
                ]
        },
        {
          type: 'inputText',
          inputListInRow:
            addressType === AppHelper.streetAddress
              ? [
                  {
                    id: 'post-number',
                    labelText: enonicData.zipCodeLabel,
                    name: 'postNumber',
                    onChange: handleZipCodeChange,
                    onBlur: handleOnBlurZipCode,
                    value: zipCode,
                    labelClassName: 'input-label-text',
                    inputWrapperClassName: 'zip-code',
                    invalid: !validZipCode && !!zipCodeErrorText,
                    errorText: zipCodeErrorText,
                    inputMode: 'numeric',
                    autoCompleteLabel: 'postal-code'
                  }
                ]
              : [
                  {
                    id: 'fastighetsbetackning',
                    labelText: enonicData.propertyNameLabel,
                    name: 'input-fastighetsbetackning',
                    onChange: handlePropertyNameChange,
                    onBlur: handleOnBlurPropertyName,
                    value: propertyKey,
                    labelClassName: 'input-label-text',
                    inputWrapperClassName: 'property-id',
                    helpText: 'T.ex. “Pålsjö 1:24”',
                    isHelpTextAlwaysVisible: true,
                    invalid: !validPropertyName && !!propertyNameErrorText,
                    errorText: propertyNameErrorText,
                    inputHelperLinkText: enonicData.propertyNameHelpText,
                    inputHelpTextId: 'property-id-more-info',
                    autoCompleteLabel: 'property-name',
                    inputHelperLinkOnClick: () =>
                      setPropertyIdExplanationModalOpen(true)
                  }
                ]
        },
        {
          type: 'inputText',
          isHidden: !showVolontaryPhoneNumber,
          inputListInRow: [
            {
              id: 'phone-number',
              labelText: 'Mobil (frivilligt)',
              name: 'input-phoneNumber',
              onChange: handleOnChangePhoneNumber,
              onBlur: handleOnBlurPhoneNumber,
              value: phoneNumber,
              className: addressType === AppHelper.propertyId ? 'mt-1' : '',
              labelClassName: 'input-label-text',
              inputWrapperClassName: 'phone-number',
              invalid: !validPhoneNumber,
              errorText: AppHelper.phoneNumberErrorText,
              helpText: AppHelper.phoneNrHelpText,
              helpTextToggleLabel: 'Visa hjälptext för Mobil',
              helpTextCloseLabel: 'Stäng hjälptext för Mobil',
              inputMode: 'numeric',
              autoCompleteLabel: 'tel'
            }
          ]
        }
      ],
      calculateBtn: {
        btnText: enonicData.calculateBtnLabel,
        onClick: gotoNextStep,
        enableTracking: true,
        disabled:
          !isValidPersonNumber ||
          isPrimaryButtonDisabled() ||
          isCprValidationLoading,
        className:
          addressType === AppHelper.propertyId
            ? 'calculation-button-property-id-input'
            : 'calculation-button mt-1',
        loading: isPropertyDataLoading || isCprValidationLoading
      }
    },
    additionalInfoLinkText: enonicData.personalDataHandlingInfoLinkText,
    additionalInfoLink: enonicData.personalDataHandlinkUrl,
    footerMessage: enonicData.footerMessage
  };

  return (
    <div id="home-villa-calculation-container">
      <CalculationWidget {...homeParams} />
      <PropertyIdExplanationModal
        isOpen={!errorObject.error && propertyIdExplanationModalOpen}
        onCloseExplanationModal={() => setPropertyIdExplanationModalOpen(false)}
        propertyIdExampleIllustration={AppHelper.getImageCDNPath(
          enonicData.imageInfo.propertyIdExampleIllustration
        )}
      />
      <CustomErrorModal
        errorObject={errorObject}
        onCloseErrorModal={onCloseErrorModal}
        propertyIdExampleIllustration={AppHelper.getImageCDNPath(
          enonicData.imageInfo.propertyIdExampleIllustration
        )}
      />
    </div>
  );
};
